import { Component, NgZone, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { User } from 'src/app/deebr/store/user.store';
import { UserService } from 'src/app/deebr/service/user.service';
import { StripoService } from 'src/app/deebr/service/stripo.service';
import { ToastService } from 'src/app/deebr/service/toast.service';
import { HtmlDimensionService } from 'src/app/deebr/service/html-dimension.service';
import { purgeStripoPortableClipboard } from 'src/app/deebr/component/pages/generator/stripo-extensions';
import { Observable, Subject, from, of, throwError } from 'rxjs';
import { catchError, debounceTime, filter, finalize, map, switchMap, take, takeUntil, tap } from 'rxjs/operators';

interface EditableModuleFields {
  name: string;
  description?: string;
  tags?: any[];
  html: string;
  css: string;
}

interface ModuleBlockPayload extends EditableModuleFields {
  key?: string;
  icon?: string | null;
  croppedIcon?: string | null;
  screenshotHtml?: string;
  width?: number;
  height?: number;
  blockType?: string;
  docType?: string;
  customFonts?: any[];
  moduleBackgroundColor?: string;
  synchronizable?: boolean;
  category?: string;
  base64?: string | null;
  metadata?: any;
  [key: string]: any;
}

interface ModuleEditorData extends ModuleBlockPayload {
  id: number;
  [key: string]: any;
}

@Component({
  selector: 'app-module-edit',
  templateUrl: './module-edit.component.html',
  styleUrls: ['./module-edit.component.scss'],
  standalone: false,
})
export class ModuleEditComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  private autosaveSubject = new Subject<void>();
  private canTriggerAutosave = false;
  private authToken: string | null = null;
  private envPrefix = 'preprod-';
  private emailId = '';
  private userIdStr = '';
  private currentModule: ModuleEditorData | null = null;

  public moduleId!: number;
  public userId!: number;
  public userName = '';
  public moduleName = '';
  public isLoading = true;
  public isSaving = false;
  public isManualSave = false;
  public showToolbox = true;
  public areHiddenElementsHidden = true;
  public selectedView: 'desktop' | 'mobile' = 'desktop';
  public stripoUserKey = '';
  public stripoTags: any[] = [];

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly userService: UserService,
    private readonly stripoService: StripoService,
    private readonly toastService: ToastService,
    private readonly zone: NgZone,
    private readonly htmlDimensionService: HtmlDimensionService,
  ) {}

  ngOnInit(): void {
    const moduleIdParam = Number(this.route.snapshot.paramMap.get('id'));
    if (!Number.isFinite(moduleIdParam) || moduleIdParam <= 0) {
      this.router.navigate(['/pages/modules']);
      return;
    }

    this.moduleId = moduleIdParam;
    this.envPrefix = window.location.hostname === 'app.deebr.co' ? 'prod-' : 'preprod-';
    this.setupAutosave();

    this.userService.getUsers().pipe(
      takeUntil(this.destroy$),
      take(1),
      switchMap((users: User[]) => {
        if (!users || users.length === 0) {
          throw new Error('Aucun utilisateur trouvé');
        }
        this.userId = users[0].id;
        this.userName = users[0].first_name;
        this.emailId = `${this.envPrefix}0`;
        this.userIdStr = `${this.envPrefix}${this.userId}`;
        return this.stripoService.getAuthToken();
      }),
      tap(({ token }) => {
        this.authToken = token;
      }),
      switchMap(() => this.loadModuleToEdit()),
      switchMap((module: ModuleEditorData) => {
        return this.loadModuleConfig().pipe(
          map((config) => ({ module, config })),
        );
      }),
      switchMap(({ module, config }) => {
        const themeVars = config?.theme?.params ?? null;
        return from(this.initializeEditor(module, themeVars));
      }),
      catchError((error) => {
        console.error('Erreur lors de l’ouverture du module:', error);
        this.toastService.error('Module', 'Impossible d’ouvrir ce module dans l’éditeur');
        this.router.navigate(['/pages/modules']);
        return of(null);
      }),
      finalize(() => {
        this.isLoading = false;
      }),
    ).subscribe();
  }

  ngOnDestroy(): void {
      if (this._origXHROpen) XMLHttpRequest.prototype.open = this._origXHROpen;
  if (this._origXHRSend) XMLHttpRequest.prototype.send = this._origXHRSend;
    this.canTriggerAutosave = false;
    this.saveModule(false).pipe(take(1)).subscribe();

    try {
      if ((window as any).UIEditor?.removeEditor) {
        (window as any).UIEditor.removeEditor();
      }
    } catch (error) {
      console.error('[ModuleEdit] Erreur removeEditor:', error);
    }

    this.destroy$.next();
    this.destroy$.complete();
  }

  goBack(): void {
    this.router.navigate(['/pages/modules']);
  }

  onModuleNameChange(name: string): void {
    this.moduleName = name;
    if (this.currentModule) {
      this.currentModule.name = name;
    }
    this.autosaveSubject.next();
  }

  saveModuleManually(): void {
    if (this.isManualSave) {
      return;
    }

    this.isManualSave = true;
    this.saveModule(true).pipe(
      finalize(() => {
        this.isManualSave = false;
      }),
      take(1),
    ).subscribe();
  }

  openHistory(): void {
    if (window.StripoEditorApi?.versionHistoryApi?.openVersionHistory) {
      window.StripoEditorApi.versionHistoryApi.openVersionHistory(
        () => {},
        () => {},
      );
    }
  }

  onSelectView(view: 'desktop' | 'mobile'): void {
    if (this.showToolbox) {
      this.selectedView = view;
      return;
    }

    if (window.StripoEditorApi?.versionHistoryApi) {
      if (view === 'desktop') {
        window.StripoEditorApi.versionHistoryApi.switchToDesktopPreview();
      } else {
        window.StripoEditorApi.versionHistoryApi.switchToMobilePreview();
      }
    }
    this.selectedView = view;
  }

  private loadModuleToEdit(): Observable<ModuleEditorData> {
    if (!this.authToken) {
      return of(this.normalizeModule({
        id: this.moduleId,
        name: `Module ${this.moduleId}`,
        html: '',
        css: '',
      }));
    }

    // 1. OPTIMISATION : On vérifie D'ABORD si les données sont dans le state
    const moduleFromState = history.state?.module;
    if (moduleFromState && Number(moduleFromState.id) === this.moduleId && moduleFromState.html && moduleFromState.css) {
      const normalized = this.normalizeModule(moduleFromState);
      this.currentModule = normalized;
      this.moduleName = normalized.name || `Module ${this.moduleId}`;

      return of(normalized);
    }

    // 2. Si on n'a rien dans le state, on tente l'appel API
    return this.stripoService.getCustomBlockById(
      this.authToken,
      this.emailId,
      this.userIdStr,
      this.userName,
      this.moduleId,
    ).pipe(
      map((response: any) => this.normalizeModule(response)),
      tap((module: ModuleEditorData) => {
        this.currentModule = module;
        this.moduleName = module.name || `Module ${this.moduleId}`;
      }),
      catchError((error) => {
        // 3. FALLBACK : On capture la 405 sans faire planter l'éditeur
        console.warn('⚠️ Appel API Stripo ignoré (Erreur 405). Création d\'un module vide local.', error.message);
        
        const fallbackModule = this.normalizeModule({
          id: this.moduleId,
          name: `Module ${this.moduleId}`,
          html: '',
          css: '',
        });
        
        this.currentModule = fallbackModule;
        this.moduleName = fallbackModule.name;
        
        // On retourne of() pour continuer la chaîne (l'éditeur s'ouvrira quand même)
        // au lieu de throwError() qui déclenchait la redirection dans le ngOnInit.
        return of(fallbackModule); 
      })
    );
  }

  private normalizeModule(payload: any): ModuleEditorData {
    const source = payload?.module ?? payload?.data ?? payload ?? {};
    return {
      id: Number(source.id ?? this.moduleId),
      name: source.name ?? '',
      description: source.description ?? '',
      tags: source.tags ?? [],
      html: source.html ?? '',
      css: source.css ?? '',
      key: source.key,
      ...source,
    };
  }

  
private _origXHROpen?: typeof XMLHttpRequest.prototype.open;
private _origXHRSend?: typeof XMLHttpRequest.prototype.send;

private patchXHRForStripo(): void {
  this._origXHROpen = XMLHttpRequest.prototype.open;
  this._origXHRSend = XMLHttpRequest.prototype.send;
  const self = this;

  XMLHttpRequest.prototype.open = function(
    method: string,
    url: string | URL,
    async: boolean = true,
    username?: string | null,
    password?: string | null
  ) {
    const urlStr = String(url);
    if (urlStr.includes('plugins.stripo.email') && urlStr.includes('/documents/') && !urlStr.includes('/screenshots/')) {
      (this as any)._isStripoApiReq = true;
    }
    if (urlStr.includes('documents/hide') && method === 'DELETE') {
      method = 'POST';
    }
    return self._origXHROpen!.call(this, method, url as string, async, username, password);
  };

  XMLHttpRequest.prototype.send = function(
    body?: Document | XMLHttpRequestBodyInit | null
  ) {
    if ((this as any)._isStripoApiReq) {
      const xhr = this;
      self.getAuthTokenCallback()((token: string) => {
        if (token) {
          XMLHttpRequest.prototype.setRequestHeader.call(xhr, 'ES-PLUGIN-AUTH', `Bearer ${token}`);
          XMLHttpRequest.prototype.setRequestHeader.call(xhr, 'ES-PLUGIN-UI-DATA', encodeURIComponent(JSON.stringify({
            emailId: `${self.envPrefix}${self.moduleId}`,
            UserId: self.userIdStr,
            username: self.userName,
          })));
          XMLHttpRequest.prototype.setRequestHeader.call(xhr, 'ES-PLUGIN-LOCALE', 'fr');
          
        }
        self._origXHRSend!.call(xhr, body);
      });
    } else {
      self._origXHRSend!.apply(this, [body]);
    }
  };
}




  private initializeEditor(module: ModuleEditorData, themeVars?: Record<string, string> | null): Promise<void> {
    return new Promise((resolve, reject) => {
      this.zone.runOutsideAngular(() => {
        try {
          (window as any).UIEditor?.removeEditor?.();
        } catch (error) {
          console.warn('[ModuleEdit] removeEditor pre-init warning:', error);
        }

        // Même protection que l'éditeur d'emails : une entrée valide du presse-papier
        // natif Stripo fait geler UIEditor au démarrage (voir purgeStripoPortableClipboard).
        purgeStripoPortableClipboard();

        const container = document.querySelector('#stripoModuleEditorContainer') as HTMLElement | null;
        this.patchXHRForStripo();
        const UIEditor = (window as any).UIEditor;

        if (!container || !UIEditor?.initEditor) {
          reject(new Error('UIEditor indisponible'));
          return;
        }

        const componentInstance = this;

        const initConfig: any = {
          codeEditorButtonSelector: '#headerCodeEditorButton',
          undoButtonSelector: '#headerUndoButton',
          redoButtonSelector: '#headerRedoButton',
          mobileViewButtonSelector: '#mobileViewButton',
          desktopViewButtonSelector: '#desktopViewButton',
          previewButtonSelector: '#previewButton',
          openModuleDetailsButtonSelector: '#openModuleDetailsButton',
          pluginMode: false,
          applicationType: 'STRIPO',
          templatePreviewIconWidth: 220,
          enableXSSSecurity: true,
          ckEditorCdn: 'https://cdn-ckeditor.stripo.email',
          codeEditorButtonId: 'headerCodeEditorButton',
          previewButtonId: 'previewButton',
          undoButtonId: 'headerUndoButton',
          redoButtonId: 'headerRedoButton',
          desktopViewButtonId: 'desktopViewButton',
          mobileViewButtonId: 'mobileViewButton',
          backButton: 'back-button',
          webPreviewButtonId: 'webPreviewButton',
          webPreviewClose: 'close',
          openModuleDetailsButtonId: 'openModuleDetailsButton',
          notifications: {},
          name: module.name || this.moduleName || `Module ${this.moduleId}`,
          userId: this.userId,
          userFullName: this.userName,
          locale: 'fr',
          permissions: {
            codeEditor: { read: true, write: true },
            appearance: { read: true, write: true },
            content: { read: true, write: true },
            entityImages: { read: true, write: true },
            projectImages: { read: true, write: true },
            versionHistory: { read: true, write: true },
            commentsAll: { read: true, write: true },
            commentsOwn: { read: true, write: true },
            commentsAllReplies: { read: true, write: true },
            modules: { read: true, write: true },
          },
          conditionsEnabled: true,
          conditionCategories: [],
          specialLinks: [],
          mergeTags: [],
          disableLineHeightsReplace: false,
          customBlocks: {
            enabled: false,
          },
          closeModulePanelOnModuleDrop: false,
          showModuleListOnClick: false,
          extensionsManagementEnabled: true,
          extensions: [],
          editorMode: 'module',
          ownHeader: false,
          editorThemeVariables: themeVars ?? {
            "primary-color": "#35a2eb",
            "secondary-color": "#F6F6F6",
            "border-radius-base": "5px",
            "customFontLink": "",
            "font-size": "12px",
            "font-family": "-apple-system, BlinkMacSystemFont, \"Segoe UI\", Roboto, \"Helvetica Neue\", Arial, \"Noto Sans\", sans-serif, \"Apple Color Emoji\", \"Segoe UI Emoji\", \"Segoe UI Symbol\", \"Noto Color Emoji\";",
            "option-panel-background-color": "#F6F6F6",
            "default-font-color": "#4d4d4d",
            "panels-border-color": "#ffffff"
          },
          metadata: {
            username: this.userName,
            avatarUrl: null,
            moduleId: this.moduleId,
            UserId: this.userIdStr,
          },

          autoSaveEnabled: false,
          forceRecreate: true,
          codeEditor: {
            hideClosedEditor: true,
          },
          panelPosition: 'BLOCKS_SETTINGS',
          brandColorPalette: [],
          defaultPaletteEnabled: true,
          customColorsEnabled: true,
          templateThemeMode: 'LIGHT',
          viewOptions: {
            showHiddenElements: true,
            mimeType: 'both',
            mergeTags: 'raw',
          },
          saveModuleInterval: 5000,
          moduleConfig: {
            synchronizable: false,
          },
          ignoreClickOutsideSelectors: ['.cdk-overlay-container'],
          html: module.html.replace(/\s*esd-custom-block-id="[^"]*"/g, ''),
          css: module.css,
          imagesLibrary: [
              {
                id: 'user',
                enabled: true,
                maxFileSize: 10 * 1024 * 1024,
                label: 'Images',
                uploadEnabled: true,

                getList: {
                  url: 'https://plugins.stripo.email/api/v1/documents/v3/documents/page',
                  params: {
                    key: `user_${this.userIdStr}`,
                    desc: true,
                    sort: 'UPLOAD_DATE',
                  },
                  baseImageLibraryUrl: '',
                },

                getInfo: {
                  url: 'https://plugins.stripo.email/api/v1/documents/v3/documents/info',
                  params: {
                    key: `user_${this.userIdStr}`,
                  },
                },

                uploadFile: {
                  url: 'https://plugins.stripo.email/api/v1/documents/v3/documents',
                  params: {
                    key: `user_${this.userIdStr}`,
                  },
                  baseImageLibraryUrl: '',
                },

                uploadBase64: {
                  url: 'https://plugins.stripo.email/api/v1/documents/v3/documents/base64',
                  params: {
                    guidParams: {
                      key: `user_${this.userIdStr}`,
                    },
                  },
                  baseImageLibraryUrl: '',
                },

                uploadLink: {
                  url: 'https://plugins.stripo.email/api/v1/documents/v3/link',
                  params: {
                    guidParams: {
                      key: `user_${this.userIdStr}`,
                    },
                  },
                  baseImageLibraryUrl: '',
                },

                removeConfig: {
                    url: 'https://plugins.stripo.email/api/v1/documents/v3/documents/hide',
                    params: {
                      key: `user_${this.userIdStr}`,  // ← directement dans params, sans guidParams
                    },
                    recoverUrl: 'https://plugins.stripo.email/api/v1/documents/v3/documents/recover',
                  },
              }
            ],
          onTokenRefreshRequest: this.getAuthTokenCallback(),
          onEvent: (type: string, params: any) => {
            console.log('[ModuleEdit] onEvent:', type, params);
          },
          onSaveStarted: () => {
            componentInstance.isSaving = true;
          },
          onSaveCompleted: () => {
            componentInstance.isSaving = false;
          },
          onDataChanged: () => {
            if (this.canTriggerAutosave) {
              this.autosaveSubject.next();
            }
          },
          onTemplateLoaded: () => {
            this.loadModuleConfig();
            window.StripoEditorApi?.viewOptionsApi?.setViewOptions({
              showHiddenElements: this.areHiddenElementsHidden,
            });
            setTimeout(() => {
              this.canTriggerAutosave = true;
            }, 2000);
            resolve();
          },
          editorFonts: {
            showDefaultStandardFonts: true,
            showDefaultNotStandardFonts: true,
            customFonts: [
              {
                name: 'Montserrat',
                fontFamily: "'Montserrat', 'helvetica neue', helvetica, arial, sans-serif",
                url: 'https://fonts.googleapis.com/css?family=Montserrat&display=swap',
              },
              {
                name: 'Jost',
                fontFamily: "'Jost', 'serif'",
                url: 'https://fonts.googleapis.com/css2?family=Jost&display=swap',
              },
            ],
          },
          onError: (error: any) => {
            console.error('[ModuleEdit] onError editor:', error);
            reject(error);
          },
        };

        UIEditor.initEditor(container, initConfig);
      });
    });
  }

  private loadModuleConfig(): Observable<any> {
    if (!this.authToken) {
      return of(null);
    }

    const prefixedEmailId = `${this.envPrefix}${this.emailId}`;
    return this.stripoService.getModuleConfig(
      this.authToken,
      prefixedEmailId,
      this.userIdStr,
      this.userName,
    ).pipe(
      tap((config) => {
        // keep your existing side-effects for tags
        if (config?.blocksLibrary?.tabs?.length > 0) {
          this.stripoUserKey = config.blocksLibrary.tabsV2[0].key;
          this.stripoService.getCustomBlocks(
            this.authToken || '',
            this.stripoUserKey,
            prefixedEmailId,
            this.userIdStr,
            this.userName,
          ).pipe(takeUntil(this.destroy$)).subscribe({
            next: (customBlocks) => {
              if (customBlocks?.tags) {
                this.stripoTags = customBlocks.tags;
              }
            },
            error: (error) => console.error('Erreur récupération custom blocks module:', error),
          });
        }
      }),
      catchError((error) => {
        console.error('Erreur récupération config module:', error);
        return of(null);
      }),
    );
  }

  private setupAutosave(): void {
    this.autosaveSubject.pipe(
      debounceTime(500),
      switchMap(() => this.saveModule(false)),
      takeUntil(this.destroy$),
    ).subscribe();
  }

  private buildModuleUpdatePayload(
    existing: ModuleEditorData,
    edits: EditableModuleFields,
    screenshotHtml: string,
    detectedBlockType: string,
    width: number,
    height: number,
    icon: string | null,
  ): ModuleBlockPayload {
    const moduleKey = `email_${this.envPrefix + this.userId}`;
    const preserved: ModuleBlockPayload = {
      ...existing,
      key: moduleKey,
      icon: icon ?? existing.icon ?? null,
      croppedIcon: icon ?? existing.croppedIcon ?? null,
      screenshotHtml,
      width,
      height,
      blockType: detectedBlockType,
      docType: existing.docType ?? 'BLOCK_ICON',
      customFonts: existing.customFonts ?? [],
      moduleBackgroundColor: existing.moduleBackgroundColor ?? '#efefef',
      synchronizable: existing.synchronizable,
      category: existing.category ?? '',
      base64: existing.base64 ?? null,
    };

    delete (preserved as any).id;
    delete (preserved as any).createdTime;
    delete (preserved as any).updatedTime;
    delete (preserved as any).createdBy;
    delete (preserved as any).updatedBy;
    delete (preserved as any).updatedByUserId;
    delete (preserved as any).updatedByUserName;

    return {
      ...preserved,
      ...edits,
      key: moduleKey,
    };
  }

  private saveModule(manual: boolean): Observable<any> {
    if (!this.authToken || !this.currentModule) {
      return of(null);
    }

    return from(this.getTemplateFromEditor()).pipe(
      switchMap(({ html, css }) => {
        let moduleHtml = this.extractModuleHtml(html);
        // Re-add esd-custom-block-id (stripped for editing to avoid SDK crash)
        if (!moduleHtml.includes('esd-custom-block-id') && moduleHtml.includes('esd-stripe')) {
          moduleHtml = moduleHtml.replace(
            /class="esd-stripe"/,
            `class="esd-stripe" esd-custom-block-id="${this.moduleId}"`,
          );
        }

        const detectedBlockType = this.detectBlockType(moduleHtml, this.currentModule?.blockType);
        const width = this.htmlDimensionService.getBlockWidth(moduleHtml, detectedBlockType);
        const screenshotHtml = this.htmlDimensionService.buildScreenshotHtml(moduleHtml, css, width, detectedBlockType);

        const editableFields: EditableModuleFields = {
          name: this.currentModule?.name || this.moduleName || `Module ${this.moduleId}`,
          description: this.currentModule?.description || '',
          tags: this.currentModule?.tags || [],
          html: moduleHtml,
          css,
        };

        const fallbackHeight = this.currentModule?.height ?? 500;

        return from(this.htmlDimensionService.calculateDimensions(screenshotHtml, width)).pipe(
          catchError(() => of({ width, height: fallbackHeight })),
          switchMap(({ height }) => {
            const screenshotPayload = {
              html: moduleHtml,
              css,
              screenshotHtml,
              width,
              height,
              docType: 'BLOCK_ICON',
              blockType: detectedBlockType,
              customFonts: [],
            };

            return this.stripoService.generateScreenshot(
              this.authToken || '',
              this.emailId,
              this.userIdStr,
              this.userName,
              screenshotPayload,
            ).pipe(
              catchError(() => of({ icon: null })),
              switchMap((res: any) => {
                const icon: string | null = res?.icon ?? null;
                const payload = this.buildModuleUpdatePayload(
                  this.currentModule as ModuleEditorData,
                  editableFields,
                  screenshotHtml,
                  detectedBlockType,
                  width,
                  height,
                  icon,
                );

                return this.stripoService.updateCustomBlock(
                  this.authToken || '',
                  this.emailId,
                  this.userIdStr,
                  this.userName,
                  this.moduleId,
                  payload,
                ).pipe(
                  tap(() => {
                    if (this.currentModule) {
                      Object.assign(this.currentModule, editableFields);
                    }
                    this.moduleName = editableFields.name;

                    try {
                      history.replaceState(
                        { ...history.state, module: { ...this.currentModule } },
                        '',
                      );
                    } catch (e) {
                      console.warn('[ModuleEdit] Impossible de mettre à jour history.state:', e);
                    }

                    if (manual) {
                      this.toastService.success('Module', 'Module sauvegardé avec succès');
                    }
                  }),
                  catchError((error) => {
                    console.error('Erreur sauvegarde module:', error);
                    if (manual) {
                      this.toastService.error('Module', 'Erreur lors de la sauvegarde du module');
                    }
                    return of(null);
                  }),
                );
              }),
            );
          }),
        );
      }),
      catchError((error) => {
        console.error('Erreur récupération template module:', error);
        if (manual) {
          this.toastService.error('Module', 'Erreur lors de la sauvegarde du module');
        }
        return of(null);
      }),
    );
  }

  private getTemplateFromEditor(): Promise<{ html: string; css: string }> {
    return new Promise((resolve, reject) => {
      const actionsApi = (window as any).StripoEditorApi?.actionsApi;

      if (!actionsApi?.getTemplateData) {
        reject(new Error('StripoEditorApi.actionsApi.getTemplateData indisponible'));
        return;
      }

      actionsApi.getTemplateData((data: any) => {
        if (!data?.html) {
          reject(new Error('Template module vide'));
          return;
        }
        resolve({ html: data.html, css: data.css });
      });
    });
  }

  private extractModuleHtml(fullHtml: string): string {
    const trimmed = fullHtml.trim();
    if (!trimmed.toLowerCase().startsWith('<html') && !trimmed.toLowerCase().startsWith('<!doctype')) {
      return fullHtml;
    }

    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(fullHtml, 'text/html');

      const moduleEl =
        doc.querySelector('td.esd-stripe') ||
        doc.querySelector('td.esd-structure') ||
        doc.querySelector('[esd-custom-block-id]') ||
        doc.body?.firstElementChild;

      if (moduleEl) {
        return moduleEl.outerHTML;
      }
    } catch (error) {
      console.warn('[ModuleEdit] Erreur extraction fragment module:', error);
    }

    return fullHtml;
  }

  private detectBlockType(moduleHtml: string, fallback?: string): string {
    const trimmed = moduleHtml.trim().toLowerCase();
    if (/class\s*=\s*["'][^"']*esd-stripe/.test(trimmed)) return 'STRIPE';
    if (/class\s*=\s*["'][^"']*esd-structure/.test(trimmed)) return 'STRUCTURE';
    if (/class\s*=\s*["'][^"']*esd-container/.test(trimmed)) return 'CONTAINER';
    return fallback || 'STRIPE';
  }

  private getAuthTokenCallback(): (callback: any) => void {
    return (callback: any) => {
      if (this.authToken && !this.isTokenExpired(this.authToken)) {
        callback(this.authToken);
      } else {
        this.getNewAuthToken(callback);
      }
    };
  }

  private isTokenExpired(token: string): boolean {
    const payload = token.split('.')[1];
    const decoded = JSON.parse(atob(payload));
    const exp = decoded.exp * 1000;
    return Date.now() > exp;
  }

  private getNewAuthToken(callback: Function): void {
    this.stripoService.getAuthToken().pipe(
      take(1),
      filter((res) => !!res?.token),
    ).subscribe({
      next: (res) => {
        this.authToken = res.token;
        callback(res.token);
      },
      error: () => {
        callback(null);
      },
    });
  }
}

declare var window: any;
